/**
 * `/report-suspicious-email` — one suspicious email, sized as what it is (19 Oct).
 *
 * Incident 940: a single suspicious email came through the Emergency form and
 * became a Priority 1. This page asks the one question that decides how urgent
 * it really is — "Did you click a link, open an attachment, or enter your
 * password?" — and the server files it from the answer: No is a low-priority
 * incident for the Security Team, Yes (or not sure) a Priority 2 for the same
 * team, with the person told to change their password now.
 *
 * Reached from the Emergency form's "What happened?" panel under the SECURITY
 * option (20 Oct; "I clicked…" arrives with `?clicked=yes`) and from the chat when
 * someone mentions a suspicious, phishing or spam email.
 */

import { CircleCheck, Icon, ShieldAlert } from "@norican/icons";
import {
  Alert,
  Button,
  Card,
  Field,
  Radio,
  RadioGroup,
  Textarea,
  Typography,
} from "@norican/react";
import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { AttachmentUploader, usePasteToAttach } from "@/components/attachments";
import { AppBreadcrumbs, ErrorState, PageContainer } from "@/components/common";
import { FilingAsLine } from "@/components/layout";
import {
  KioskTicketNumberNote,
  type TicketCreateRequest,
  type TicketDetail,
  ticketReferenceKey,
  useCreateTicket,
} from "@/features/tickets";
import { newIdempotencyKey } from "@/features/tickets/api/operations";
import { useDocumentTitle } from "@/hooks";
import { useLanguage, useT } from "@/i18n";
import { logger } from "@/lib/logger";

import { FiledPriority } from "./filed-priority";
import { initialClickedAnswer } from "./lib/priority";

const ANSWERS = ["NO", "YES"] as const;
type Answer = (typeof ANSWERS)[number];

/** Give up waiting for the filing after this long and show the error. */
const CREATE_TIMEOUT_MS = 30_000;

export function SuspiciousEmailPage() {
  const { t } = useT("tickets");
  const { language } = useLanguage();
  const create = useCreateTicket();
  const { search } = useLocation();

  /* 20 Oct: the Emergency form's "I clicked a link…" choice opens this page
     with `?clicked=yes`, so "Yes" is already chosen (and can be changed). */
  const [answer, setAnswer] = useState<Answer | null>(() => initialClickedAnswer(search));
  const [text, setText] = useState("");
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [filed, setFiled] = useState<{ ticket: TicketDetail; clicked: boolean } | null>(null);
  const paste = usePasteToAttach({ disabled: create.isPending });
  /* Same as the Emergency form: one Idempotency-Key per body, so a retry after
     a timeout replays the first report instead of filing a second. */
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);
  const locked = create.isPending;
  const missingHintId = useId();

  const filedHeadingRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (filed !== null) {
      filedHeadingRef.current?.focus();
    }
  }, [filed]);

  useDocumentTitle(t("suspiciousEmail.title"));

  const handleSubmit = () => {
    if (answer === null || uploading || create.isPending) {
      return;
    }
    const clicked = answer === "YES";
    const body: TicketCreateRequest = {
      kind: "INCIDENT",
      description: text.trim() === "" ? t("suspiciousEmail.defaultDescription") : text.trim(),
      body_lang: language,
      /* The server sets the priority, the Security Team, the classification
         and the title from this answer. */
      suspicious_email: { clicked },
      category_id: null,
      service_id: null,
      attachment_ids: attachmentIds,
    };
    const fingerprint = JSON.stringify(body);
    if (attempt.current === null || attempt.current.fingerprint !== fingerprint) {
      attempt.current = { fingerprint, key: newIdempotencyKey() };
    }
    logger.debug("[suspicious-email] filing", { clicked, attachments: attachmentIds.length });
    create.mutate(
      {
        body,
        idempotencyKey: attempt.current.key,
        signal: AbortSignal.timeout(CREATE_TIMEOUT_MS),
      },
      {
        onSuccess: (ticket) => {
          logger.debug("[suspicious-email] filed", ticket.ticket_number);
          attempt.current = null;
          setFiled({ ticket, clicked });
        },
        onError: (error) => logger.warn("[suspicious-email] filing failed", error),
      },
    );
  };

  const linkClass = "text-accent-fg-subtle font-medium underline-offset-2 hover:underline";

  return (
    <PageContainer
      title={t("suspiciousEmail.title")}
      subtitle={filed === null ? t("suspiciousEmail.subtitle") : undefined}
      breadcrumbs={<AppBreadcrumbs items={[{ label: t("suspiciousEmail.title") }]} />}
    >
      <div className="flex max-w-[var(--norican-size-container-md)] flex-col gap-6">
        {filed === null ? (
          <Card padding="lg">
            <div className="flex flex-col gap-5" onPaste={paste.onPaste}>
              <Field
                name="clicked"
                label={t("suspiciousEmail.clickedLabel")}
                required
                disabled={locked}
              >
                <RadioGroup
                  value={answer ?? ""}
                  onValueChange={(value) => setAnswer(value as Answer)}
                  disabled={locked}
                  className="flex flex-col gap-2"
                >
                  {ANSWERS.map((option) => (
                    <Radio.Root
                      key={option}
                      value={option}
                      label={t(`suspiciousEmail.clicked.${option}`)}
                    />
                  ))}
                </RadioGroup>
              </Field>

              {answer === "YES" ? (
                <Alert tone="danger" title={t("suspiciousEmail.yesWarning")} />
              ) : null}
              {answer === "NO" ? <Alert tone="info" title={t("suspiciousEmail.noHint")} /> : null}

              <Field
                name="what"
                label={t("suspiciousEmail.whatLabel")}
                hint={t("suspiciousEmail.whatHint")}
                disabled={locked}
              >
                <Textarea
                  rows={3}
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  disabled={locked}
                  placeholder={t("suspiciousEmail.whatPlaceholder")}
                />
              </Field>

              <Field name="evidence" label={t("suspiciousEmail.attachLabel")} disabled={locked}>
                <AttachmentUploader
                  ref={paste.ref}
                  id="evidence"
                  onChange={setAttachmentIds}
                  onPendingChange={setUploading}
                  disabled={create.isPending}
                />
              </Field>

              {create.isError ? (
                <ErrorState variant="inline" error={create.error} offerHelpdeskContact />
              ) : null}

              <div className="flex flex-col gap-2">
                <FilingAsLine />
                <Button
                  type="button"
                  tone={answer === "YES" ? "danger" : "accent"}
                  size="lg"
                  loading={create.isPending || uploading}
                  disabled={answer === null || uploading}
                  aria-describedby={answer === null ? missingHintId : undefined}
                  startIcon={<Icon icon={ShieldAlert} size="sm" />}
                  onClick={handleSubmit}
                  className="self-start"
                >
                  {uploading ? t("emergency.submitUploading") : t("suspiciousEmail.submit")}
                </Button>
                {answer === null ? (
                  <Typography as="p" id={missingHintId} variant="caption" tone="muted">
                    {t("suspiciousEmail.missingAnswer")}
                  </Typography>
                ) : null}
              </div>

              <Typography as="p" variant="caption" tone="muted" className="text-pretty">
                {t("suspiciousEmail.biggerThanThat")}{" "}
                <Link to="/emergency" className={linkClass}>
                  {t("emergency.title")}
                </Link>
              </Typography>
            </div>
          </Card>
        ) : (
          <>
            <Card padding="lg" className="border-success-border bg-success-subtle/40">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Icon icon={CircleCheck} size="md" className="text-success-fg shrink-0" />
                  <Typography
                    as="h2"
                    variant="heading-4"
                    ref={filedHeadingRef}
                    tabIndex={-1}
                    className="focus:outline-none"
                  >
                    {filed.clicked
                      ? t("suspiciousEmail.filedTitleYes")
                      : t("suspiciousEmail.filedTitleNo")}
                  </Typography>
                </div>
                <div className="flex flex-col gap-1">
                  <Typography as="span" variant="caption" tone="muted">
                    {t("emergency.referenceLabel")}
                  </Typography>
                  <Link
                    to={filed.ticket.url_path}
                    className="text-accent-fg-subtle font-mono text-2xl font-semibold tracking-tight underline-offset-4 hover:underline"
                  >
                    {t(ticketReferenceKey(filed.ticket.kind, "id"), {
                      number: filed.ticket.ticket_number,
                    })}
                  </Link>
                </div>
                <FiledPriority code={filed.ticket.priority_code} />
              </div>
            </Card>

            <KioskTicketNumberNote number={filed.ticket.ticket_number} />

            {filed.clicked ? (
              <Alert tone="danger" title={t("suspiciousEmail.guidanceYes.password")}>
                <ul className="list-disc pl-5">
                  <li>{t("suspiciousEmail.guidanceYes.contact")}</li>
                  <li>{t("suspiciousEmail.guidanceYes.dontClick")}</li>
                </ul>
              </Alert>
            ) : (
              <Alert tone="info" title={t("suspiciousEmail.guidanceNo.title")}>
                <ul className="list-disc pl-5">
                  <li>{t("suspiciousEmail.guidanceNo.dontClick")}</li>
                  <li>{t("suspiciousEmail.guidanceNo.keep")}</li>
                </ul>
              </Alert>
            )}

            <Typography as="p" variant="body-sm" className="text-pretty">
              {t("suspiciousEmail.chatNudge")}{" "}
              <Link to="/ask" className={linkClass}>
                {t("emergency.backToHelpDesk")}
              </Link>
            </Typography>
          </>
        )}
      </div>
    </PageContainer>
  );
}
